import { providerProductService } from '../../../services/ProviderProduct'
import { ActionsUnion, IMap, IProviderProductFilters, IProviderProductObject } from '../../../types/index'
import { createAction } from '../helpers'
import * as constants from '../../constants/index'
import { constant } from 'lodash'
import history from 'src/history'

export const actionCreators = {
  setCurrentProductIds: (ids: string[]) => createAction(constants.SET_CURRENT_PRODUCT_IDS, ids),
  filterProducts: (query: string) => createAction(constants.PRODUCT_FILTER, query),

  productFetchRequest: () => createAction(constants.PRODUCT_FETCH_REQUEST),
  productFetchSuccess: (product: IProviderProductObject) => createAction(constants.PRODUCT_FETCH_SUCCESS, product),
  productFetchFail: (error: string) => createAction(constants.PRODUCT_FETCH_FAIL, error),

  productActivateRequest: () => createAction(constants.PROVIDER_PRODUCT_ACTIVATE_REQUEST),
  productActivateSuccess: (product: IProviderProductObject) =>
    createAction(constants.PROVIDER_PRODUCT_ACTIVATE_SUCCESS, product),
  productActivateFail: (error: string) => createAction(constants.PROVIDER_PRODUCT_ACTIVATE_REQUEST, error),

  productCreateRequest: () => createAction(constants.PROVIDER_PRODUCT_CREATE_REQUEST),
  productCreateSuccess: (product: IProviderProductObject) =>
    createAction(constants.PROVIDER_PRODUCT_CREATE_SUCCESS, product),
  productCreateFail: (error: string) => createAction(constants.PROVIDER_PRODUCT_CREATE_FAIL, error),

  productDeactivateRequest: () => createAction(constants.PROVIDER_PRODUCT_DEACTIVATE_REQUEST),
  productDeactivateSuccess: (product: IProviderProductObject) =>
    createAction(constants.PROVIDER_PRODUCT_DEACTIVATE_SUCCESS, product),
  productDeactivateFail: (error: string) => createAction(constants.PROVIDER_PRODUCT_DEACTIVATE_FAIL, error),

  productRemoveRequest: () => createAction(constants.PROVIDER_PRODUCT_REMOVE_REQUEST),
  productRemoveSuccess: (product: IProviderProductObject) =>
    createAction(constants.PROVIDER_PRODUCT_REMOVE_SUCCESS, product),
  productRemoveFail: (error: string) => createAction(constants.PROVIDER_PRODUCT_REMOVE_FAIL, error),

  productSaveRequest: () => createAction(constants.PROVIDER_PRODUCT_SAVE_REQUEST),
  productSaveSuccess: (product: IProviderProductObject) =>
    createAction(constants.PROVIDER_PRODUCT_SAVE_SUCCESS, product),
  productSaveFail: (error: string) => createAction(constants.PROVIDER_PRODUCT_SAVE_FAIL, error),

  productListRequest: () => createAction(constants.PROVIDER_PRODUCT_LIST_REQUEST),
  productListSuccess: (products: IMap<IProviderProductObject>) =>
    createAction(constants.PROVIDER_PRODUCT_LIST_SUCCESS, products),
  productListCount: (count: number) => createAction(constants.PROVIDER_PRODUCT_LIST_SUCCESS_COUNT, count),
  productListFail: (error: string) => createAction(constants.PROVIDER_PRODUCT_LIST_FAIL, error),

  //Non-async actions
  productActivateModalShow: (product: IProviderProductObject) =>
    createAction(constants.PROVIDER_PRODUCT_ACTIVATE_MODAL_SHOW, product),
  productActivateModalHide: () => createAction(constants.PROVIDER_PRODUCT_ACTIVATE_MODAL_HIDE),
  productDeactivateModalShow: (product: IProviderProductObject) =>
    createAction(constants.PROVIDER_PRODUCT_DEACTIVATE_MODAL_SHOW, product),
  productDeactivateModalHide: () => createAction(constants.PROVIDER_PRODUCT_DEACTIVATE_MODAL_HIDE),
  productDeleteModalShow: (product: IProviderProductObject) =>
    createAction(constants.PROVIDER_PRODUCT_DELETE_MODAL_SHOW, product),
  productDeleteModalHide: () => createAction(constants.PROVIDER_PRODUCT_DELETE_MODAL_HIDE),
  update: (product: IProviderProductObject) => createAction(constants.PROVIDER_PRODUCT_UPDATE, product),

  updateProductFilters: (filters: IProviderProductFilters) => createAction(constants.UPDATE_PRODUCT_FILTERS, filters)
}

export type IProviderProductAction = ActionsUnion<typeof actionCreators>

export const fetchProduct = (productId: string) => (dispatch: any) => {
  const { productFetchRequest, productFetchSuccess, productFetchFail } = actionCreators
  dispatch(productFetchRequest())
  return providerProductService.fetchOne(productId).then(
    (response: IProviderProductObject) => {
      dispatch(productFetchSuccess(response))
    },
    error => {
      dispatch(productFetchFail(error))
    }
  )
}

export const activateProduct = (productId: string) => (dispatch: any) => {
  const { productActivateRequest, productActivateSuccess, productActivateFail } = actionCreators
  dispatch(productActivateRequest())

  return providerProductService.activateOne(productId).then(
    (response: IProviderProductObject) => {
      dispatch(productActivateSuccess(response))
    },
    error => {
      dispatch(productActivateFail(error))
    }
  )
}

export const createProduct = (product: IProviderProductObject) => (dispatch: any) => {
  const { productCreateRequest, productCreateSuccess, productCreateFail } = actionCreators
  dispatch(productCreateRequest())

  return providerProductService.createOne(product).then(
    (response: IProviderProductObject) => {
      dispatch(productCreateSuccess(response))
    },
    error => {
      dispatch(productCreateFail(error))
    }
  )
}

export const deactivateProduct = (productId: string, deactivationDate: Date) => (dispatch: any) => {
  const { productDeactivateRequest, productDeactivateSuccess, productDeactivateFail } = actionCreators
  dispatch(productDeactivateRequest())

  return providerProductService.deactivateOne(productId, deactivationDate).then(
    (response: IProviderProductObject) => {
      dispatch(productDeactivateSuccess(response))
    },
    error => {
      dispatch(productDeactivateFail(error))
    }
  )
}

export const removeProduct = (product: IProviderProductObject) => (dispatch: any) => {
  const { productRemoveRequest, productRemoveSuccess, productRemoveFail } = actionCreators
  dispatch(productRemoveRequest())

  return providerProductService.removeOne(product.id).then(
    () => {
      dispatch(productRemoveSuccess(product))
    },
    error => {
      dispatch(productRemoveFail(error))
    }
  )
}

export const saveProduct = (product: IProviderProductObject) => (dispatch: any) => {
  const { productSaveRequest, productSaveSuccess, productSaveFail } = actionCreators
  dispatch(productSaveRequest())

  return providerProductService.saveOne(product).then(
    (response: IProviderProductObject) => {
      dispatch(productSaveSuccess(response))
      return response
    },
    error => {
      dispatch(productSaveFail(error))
    }
  )
}

export const fetchProductsByOrganizationId = (dto: IProviderProductFilters) => (dispatch: any) => {
  const { productListRequest, productListSuccess, productListFail, productListCount } = actionCreators
  dispatch(productListRequest)
  return providerProductService.getManyByOrganizationId(dto).then(
    (products: { result: IProviderProductObject[]; count: number }) => {
      const productsIMap = {}
      products.result.forEach(p => {
        productsIMap[p.id] = p
      })
      dispatch(productListSuccess(productsIMap))
      dispatch(productListCount(products.count))
      //todo: map total to new action creator
    },
    ({ data }) => {
      dispatch(productListFail(data.statusText))
    }
  )
}



// WEBPACK FOOTER //
// ./src/state/actions/provider-products/index.ts